import {
  ASK_ANONYMOUSLY_RELAY_LABEL,
  ANONYMOUS_ROUTING_CHANNEL_LABEL,
} from '@/constants/inquiryDisplay';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getInquirySubject } from '@/util/getInquirySubject';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import {
  getInquiryDisplayParticipant,
  getInquiryTitleExpert,
  isAnonymousPrimumRoutedInquiry,
} from '@/util/inquiryDisplay';
import { isSameUserId } from '@/utils/isSameUserId';

export function deriveNavigationParams(
  props: InquiryV2,
  effectiveUserId: string | undefined,
  isAnonymousInquiryGroupingEnabled: boolean
) {
  const { author, recipient, origin } = props;

  if (!author?.id || !recipient?.id) {
    return null;
  }

  const otherParticipant = isSameUserId(author.id, effectiveUserId) ? recipient : author;

  const titleExpert = getInquiryTitleExpert(props, isAnonymousInquiryGroupingEnabled);

  const anonymousPrimumRouting = isAnonymousPrimumRoutedInquiry(props);

  const isAskAnonymouslyRelayView =
    anonymousPrimumRouting && !isSameUserId(author.id, effectiveUserId);

  const displayParticipant = getInquiryDisplayParticipant(
    props,
    otherParticipant,
    isAnonymousInquiryGroupingEnabled
  );

  const headerParticipant = isAskAnonymouslyRelayView ? author : displayParticipant;

  const { name: headerName, nameWithoutDoctor: headerNameNoDoctor } = getProfileNameAndInitials({
    firstName: headerParticipant.firstName,
    lastName: headerParticipant.lastName,
    identifiesAsDoctor: headerParticipant.identifiesAsDoctor,
  });

  const headerAvatarUrl = getAvatarUrl(headerParticipant.avatar);

  const {
    name: derivedName,
    nameWithoutDoctor,
    formalName,
  } = getProfileNameAndInitials({
    firstName: displayParticipant.firstName,
    lastName: displayParticipant.lastName,
    identifiesAsDoctor: displayParticipant.identifiesAsDoctor,
  });

  const isSupportConversation = origin === 'primum_support';

  const isMentorInquiry = props.title?.type === 'mentor_inquiry';

  /**
   * Fellows Corner submits every mentor inquiry under one fixed cancer-focus
   * subject, so it never varies inquiry to inquiry and can't help anyone scan
   * their inbox. The other participant's name is the one fact that actually
   * differs, so it takes the subject slot the same way a support
   * conversation's does.
   */
  const subject =
    isSupportConversation || isMentorInquiry
      ? getProfileNameAndInitials({
          firstName: otherParticipant.firstName,
          lastName: otherParticipant.lastName,
          identifiesAsDoctor: otherParticipant.identifiesAsDoctor,
        }).name
      : getInquirySubject(props.title);

  const avatarUrl = getAvatarUrl(displayParticipant.avatar);

  return {
    otherParticipant,
    titleExpert,
    anonymousPrimumRouting,
    isAskAnonymouslyRelayView,
    displayParticipant,
    headerParticipant,
    headerName,
    headerNameNoDoctor,
    headerAvatarUrl,
    derivedName,
    nameWithoutDoctor,
    formalName,
    isSupportConversation,
    subject,
    avatarUrl,
  };
}

type BuildInquiryChatParamsOptions = {
  effectiveUserId: string | undefined;
  isAnonymousInquiryGroupingEnabled: boolean;
  canMarkOnYourHonor: boolean;
  /** Every conversation in the row's group, the open one included, which the chat shows as tabs. */
  groupConversationIds?: string[];
};

/**
 * The route params that open an inquiry's conversation, shared by the flat inbox row and the staff
 * case card so a thread opened from either lands on the same chat. Null when the row lacks the
 * participants the chat needs.
 */
export function buildInquiryChatParams(
  inquiry: InquiryV2,
  currentTab: string,
  {
    effectiveUserId,
    isAnonymousInquiryGroupingEnabled,
    canMarkOnYourHonor,
    groupConversationIds,
  }: BuildInquiryChatParamsOptions
) {
  const derived = deriveNavigationParams(
    inquiry,
    effectiveUserId,
    isAnonymousInquiryGroupingEnabled
  );

  if (!derived) {
    return null;
  }

  const {
    otherParticipant,
    titleExpert,
    isAskAnonymouslyRelayView,
    headerName,
    headerNameNoDoctor,
    headerAvatarUrl,
    derivedName,
    isSupportConversation,
    subject: subjectForChat,
    anonymousPrimumRouting,
  } = derived;

  let chatHeaderName = derivedName;

  if (titleExpert) {
    chatHeaderName = isAskAnonymouslyRelayView
      ? ASK_ANONYMOUSLY_RELAY_LABEL
      : ANONYMOUS_ROUTING_CHANNEL_LABEL;
  }

  const siblingConversationIds = groupConversationIds?.filter(
    (siblingId) => siblingId !== inquiry.conversationId
  );

  return {
    id: inquiry.conversationId,
    recipientId: otherParticipant.id.toString(),
    name: chatHeaderName,
    nameWithoutDoctor: headerNameNoDoctor,
    subject: titleExpert ? headerName : subjectForChat,
    tab: currentTab,
    markReadOnOpen: '1',
    ...(headerAvatarUrl ? { avatarUrl: headerAvatarUrl } : undefined),
    ...(inquiry.referenceInquiryIds?.[0]
      ? { referenceConversationId: inquiry.referenceInquiryIds[0] }
      : undefined),
    ...(siblingConversationIds?.length
      ? { siblingConversationIds: siblingConversationIds.join(',') }
      : undefined),
    ...(anonymousPrimumRouting && isAnonymousInquiryGroupingEnabled
      ? { anonymous: '1' }
      : undefined),
    ...(isSupportConversation && !anonymousPrimumRouting ? { support: '1' } : undefined),
    closed: inquiry.inquiryStatus === 'closed' ? '1' : undefined,
    onYourHonor: canMarkOnYourHonor ? '1' : undefined,
  };
}
